/**
 * Busca dos dados das estações.
 *
 * Duas origens, de propósito:
 *
 * - **Grade de 24 h** (`AmaView-dados`): cache publicado no GitHub Pages, com
 *   CORS liberado. É o que alimenta a camada animada. Nenhuma chamada ao
 *   CEMADEN parte do navegador, então o número de usuários não consome a cota
 *   de 12 req/min da conta usada na ingestão.
 * - **Série horária de UMA estação** (`mapservices.cemaden.gov.br`): buscada
 *   sob demanda quando alguém clica num ponto. Esse endpoint responde com
 *   `Access-Control-Allow-Origin: *` e não pede credencial — é o mesmo que o
 *   Mapa Interativo do CEMADEN usa. Uma requisição por clique, direto na
 *   fonte, sempre com o dado mais recente.
 */
import { fetchWithRetry } from '../lib/fetchRetry';
import { lonLatToMap } from '../map/projection';
import type { Dados, EstacaoCrua, Grade, Grandeza, SerieHoraria } from './types';

/**
 * Onde o pipeline publica (ver repositório `AmaView-dados`).
 *
 * `VITE_DADOS_URL` aponta para outro lugar em desenvolvimento (ex.: a saída
 * local do pipeline). É uma URL pública, nunca um segredo — o token do CEMADEN
 * vive só no workflow de ingestão e jamais chega ao navegador.
 */
export const BASE_DADOS =
  import.meta.env.VITE_DADOS_URL || 'https://helvecioneto.github.io/AmaView-dados/cemaden';

const MAPSERVICES = 'https://mapservices.cemaden.gov.br/MapaInterativoWS/resources';

export const CEMADEN_ATTRIBUTION =
  'Chuva: <a href="https://www.gov.br/cemaden/pt-br" target="_blank" rel="noopener">Rede Observacional do CEMADEN/MCTI</a>';

const GRANDEZAS: readonly string[] = ['chuva10min', 'acum24h'];

function ehGrandeza(v: unknown): v is Grandeza {
  return typeof v === 'string' && GRANDEZAS.includes(v);
}

function json(url: string, signal?: AbortSignal): Promise<unknown> {
  return fetchWithRetry<unknown>(url, {}, { signal, read: (r) => r.json() as Promise<unknown> });
}

/**
 * Cadastro + grade. As duas descidas são paralelas, e a grade é indexada pelo
 * código da estação porque a ordem das linhas pode mudar entre publicações
 * (estações entram e saem da rede).
 */
export async function carregarDados(signal?: AbortSignal): Promise<Dados> {
  const [cruEstacoes, cruGrade] = await Promise.all([
    json(`${BASE_DADOS}/estacoes.json`, signal),
    json(`${BASE_DADOS}/serie.json`, signal),
  ]);
  return lerDados(cruEstacoes, cruGrade);
}

/** O cadastro e a grade como o pipeline publica (também o recorte guardado no retrato da denúncia). */
export function lerDados(cruEstacoes: unknown, cruGrade: unknown): Dados {
  const e = cruEstacoes as { estacoes?: EstacaoCrua[]; gerado?: string; modo?: string };
  if (!Array.isArray(e.estacoes)) throw new Error('estacoes.json sem a lista `estacoes`');

  const estacoes = e.estacoes
    .filter((x) => Number.isFinite(x.y) && Number.isFinite(x.x))
    .map((x) => {
      const [mx, my] = lonLatToMap(x.x, x.y);
      return {
        cod: String(x.c),
        id: Number.isFinite(x.i) ? Number(x.i) : null,
        nome: String(x.n ?? ''),
        uf: String(x.u ?? ''),
        municipio: String(x.m ?? ''),
        codibge: Number.isFinite(x.b) ? Number(x.b) : null,
        lat: Number(x.y),
        lon: Number(x.x),
        mx,
        my,
      };
    })
    // Pontos além do limbo da Terra não têm coordenada no mapa do GOES.
    .filter((x) => Number.isFinite(x.mx) && Number.isFinite(x.my));

  const grade = lerGradePublicada(cruGrade);
  const linhaDe = new Map<string, number>();
  grade?.codigos.forEach((c, i) => linhaDe.set(c, i));

  const gerado = Date.parse(String(e.gerado ?? ''));

  return {
    estacoes,
    grade,
    linhaDe,
    gerado: Number.isFinite(gerado) ? gerado : null,
    modo: typeof e.modo === 'string' ? e.modo : null,
  };
}

/** Valida a grade publicada. Formato inesperado vira `null`, não exceção. */
function lerGradePublicada(cru: unknown): Grade | null {
  const g = cru as Partial<Grade> & { v?: unknown; estacoes?: unknown };
  if (!Array.isArray(g.v) || !Array.isArray(g.estacoes)) return null;
  if (!Number.isFinite(g.t0) || !Number.isFinite(g.slots) || !Number.isFinite(g.passoMin)) return null;
  if (g.v.length !== g.estacoes.length) return null;

  const slots = Number(g.slots);
  const v = g.v as number[][];
  if (v.some((linha) => !Array.isArray(linha) || linha.length !== slots)) return null;

  return {
    grandeza: ehGrandeza(g.grandeza) ? g.grandeza : 'acum24h',
    t0: Number(g.t0),
    passoMin: Number(g.passoMin),
    slots,
    codigos: (g.estacoes as string[]).map(String),
    v,
  };
}

/**
 * Série horária de uma estação, direto do CEMADEN (sem token, CORS liberado).
 *
 * `horas` = 24 devolve as últimas 24 h. A resposta traz uma matriz
 * `[dia][hora]`, com `null` nas horas fora do intervalo coberto.
 */
export async function serieHoraria(idEstacao: number, horas = 24, signal?: AbortSignal): Promise<SerieHoraria> {
  const j = (await json(
    `${MAPSERVICES}/horario/${idEstacao}/${Math.max(1, horas) - 1}`,
    signal,
  )) as {
    horarios?: unknown;
    datas?: unknown;
    acumulados?: unknown;
  };
  const horarios = Array.isArray(j.horarios) ? j.horarios.map(String) : [];
  const datas = Array.isArray(j.datas) ? j.datas.map(String) : [];
  const acumulados = Array.isArray(j.acumulados)
    ? (j.acumulados as unknown[]).map((linha) =>
        Array.isArray(linha) ? linha.map((x) => (Number.isFinite(Number(x)) ? Number(x) : null)) : [],
      )
    : [];
  return { horas: horarios, datas, acumulados };
}

/** Página do CEMADEN com o gráfico completo da estação (abre em nova aba). */
export function urlGrafico(idEstacao: number, uf: string): string {
  return `https://resources.cemaden.gov.br/graficos/interativo/grafico_CEMADEN.php?idpcd=${idEstacao}&uf=${encodeURIComponent(uf)}`;
}
